import { useMemo } from "react";
import {
  useListProjects, useListClientInvoices,
  useListClientReceipts, useListScInvoices, useListScPayments,
  useListSupplierInvoices, useListSupplierPayments,
  useListBudget, useListPayroll,
  type Company,
} from "@workspace/api-client-react";
import {
  TrendingUp, AlertTriangle, DollarSign,
  BarChart2, ShieldAlert, Landmark, Receipt,
  Activity, Briefcase, Clock,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fc, N, separateAdvances } from "@/lib/finance";
import { type PlanFeatures } from "@/lib/plans";
import { UpgradeTeaser } from "@/components/UpgradeTeaser";
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";

interface CFODashboardTabProps {
  companyId: string;
  company: Company;
  effectiveFeatures: PlanFeatures;
  effectivePlan: string;
}

const COST_COLORS = { sc: '#3b82f6', supplier: '#8b5cf6', payroll: '#f97316' };
const STATUS_COLOR = {
  Healthy: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
  Watch:   'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  'At Risk': 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400',
};

export default function CFODashboardTab({ companyId, company, effectiveFeatures, effectivePlan }: CFODashboardTabProps) {
  const cur = company?.currency;
  const isProOrGroup = effectivePlan === "pro" || effectivePlan === "group";
  const tickFmt = (v: number) => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}M` : v >= 1_000 ? `${(v/1_000).toFixed(0)}K` : String(v);

  const { data: projects      = [] } = useListProjects({ companyId });
  const { data: invoices      = [] } = useListClientInvoices({ companyId });
  const { data: receipts      = [] } = useListClientReceipts({ companyId });
  const { data: scInvoices    = [] } = useListScInvoices({ companyId });
  const { data: scPayments    = [] } = useListScPayments({ companyId });
  const { data: supplierInvoices = [] } = useListSupplierInvoices({ companyId });
  const { data: supplierPayments = [] } = useListSupplierPayments({ companyId });
  const { data: budgetLines   = [] } = useListBudget({ companyId });
  const { data: payrollAll    = [] } = useListPayroll({ companyId });

  const c = useMemo(() => {
    const now = Date.now();

    // ── Payment lookup maps ──────────────────────────────────────────────
    const receivedByInv: Record<string, number> = {};
    for (const r of receipts) {
      if (r.tp === 'Advance mobilisation' || r.tp === 'Advance') continue;
      receivedByInv[r.invRef] = (receivedByInv[r.invRef] ?? 0) + N(r.amount);
    }
    const scPaidByInv: Record<string, number> = {};
    for (const p of scPayments) {
      if (p.tp === 'Advance') continue;
      scPaidByInv[p.invRef] = (scPaidByInv[p.invRef] ?? 0) + N(p.amount);
    }
    const suppPaidByInv: Record<string, number> = {};
    for (const p of supplierPayments) suppPaidByInv[p.invRef] = (suppPaidByInv[p.invRef] ?? 0) + N(p.amount);

    // ── Portfolio totals ─────────────────────────────────────────────────
    const { progressTotal: totalCollected } = separateAdvances(receipts);
    const totalContractValue = projects.filter(p => p.status === 'Active').reduce((s, p) => s + N(p.ctrVal), 0);
    const totalInvoiced   = invoices.reduce((s, i) => s + N(i.gross), 0);
    const totalScClaims   = scInvoices.reduce((s, i) => s + N(i.gross), 0);
    const totalSuppInv    = supplierInvoices.reduce((s, i) => s + N(i.gross), 0);
    const totalPayroll    = payrollAll.reduce((s, p) => s + N(p.basic) + N(p.house) + N(p.trans) + N(p.othr) - N(p.deduc), 0);
    const totalScPaid     = scPayments.filter(p => p.tp !== 'Advance').reduce((s, p) => s + N(p.amount), 0);
    const totalSuppPaid   = supplierPayments.reduce((s, p) => s + N(p.amount), 0);
    const totalCostsPaid  = totalScPaid + totalSuppPaid + totalPayroll;
    const totalDirectCosts = totalScClaims + totalSuppInv + totalPayroll;

    const billingRate     = totalContractValue > 0 ? (totalInvoiced / totalContractValue) * 100 : 0;
    const collectionRate  = totalInvoiced > 0 ? (totalCollected / totalInvoiced) * 100 : 0;
    const grossMarginPct  = totalInvoiced > 0 ? ((totalInvoiced - totalDirectCosts) / totalInvoiced) * 100 : 0;

    const totalArBalance = invoices.reduce((s, inv) => {
      const bal = N(inv.gross) - (receivedByInv[inv.id] ?? 0);
      return s + Math.max(0, bal);
    }, 0);
    const totalScOutstanding   = scInvoices.reduce((s, inv) => Math.max(0, N(inv.gross) - (scPaidByInv[inv.id] ?? 0)) + s, 0);
    const totalSuppOutstanding = supplierInvoices.reduce((s, inv) => Math.max(0, N(inv.gross) - (suppPaidByInv[inv.id] ?? 0)) + s, 0);
    const totalApBalance = totalScOutstanding + totalSuppOutstanding;

    // ── DSO ──────────────────────────────────────────────────────────────
    const dsoNum = invoices.reduce((s, inv) => {
      const bal = N(inv.gross) - (receivedByInv[inv.id] ?? 0);
      if (bal <= 0.01) return s;
      const d = inv.dueDate || inv.date;
      return s + bal * (d ? Math.max(0, Math.floor((now - new Date(d).getTime()) / 86400000)) : 0);
    }, 0);
    const dso = totalArBalance > 0 ? Math.round(dsoNum / totalArBalance) : 0;

    // ── AR Aging buckets ─────────────────────────────────────────────────
    const arAging = { d30: 0, d60: 0, d90: 0, d90p: 0 };
    for (const inv of invoices) {
      const bal = N(inv.gross) - (receivedByInv[inv.id] ?? 0);
      if (bal <= 0.01) continue;
      const d = inv.dueDate || inv.date;
      const days = d ? Math.max(0, Math.floor((now - new Date(d).getTime()) / 86400000)) : 0;
      if      (days <= 30) arAging.d30  += bal;
      else if (days <= 60) arAging.d60  += bal;
      else if (days <= 90) arAging.d90  += bal;
      else                 arAging.d90p += bal;
    }

    // ── Budget utilization ───────────────────────────────────────────────
    const totalBudget = budgetLines.reduce((s, b) => s + N(b.appBgt), 0);
    const totalActual = budgetLines.reduce((s, b) => s + N(b.actl), 0);
    const budgetUtil  = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;

    // ── Tax summary ──────────────────────────────────────────────────────
    const taxCollected = invoices.reduce((s, inv) => s + N(inv.gross) * N(inv.vatRate), 0);
    const taxPaidSC    = scInvoices.reduce((s, inv) => s + N(inv.gross) * N(inv.vatRate), 0);
    const taxPaidSupp  = supplierInvoices.reduce((s, inv) => s + N(inv.gross) * N(inv.vatRate ?? 0), 0);
    const netTaxPayable = taxCollected - taxPaidSC - taxPaidSupp;

    // ── Cash snapshot ────────────────────────────────────────────────────
    const arUnder60 = invoices.reduce((s, inv) => {
      const bal = N(inv.gross) - (receivedByInv[inv.id] ?? 0);
      if (bal <= 0.01) return s;
      const d = inv.dueDate || inv.date;
      const days = d ? Math.max(0, Math.floor((now - new Date(d).getTime()) / 86400000)) : 0;
      return days < 60 ? s + bal : s;
    }, 0);
    const netCashPosition = arUnder60 - totalApBalance;

    // ── Monthly trend ─────────────────────────────────────────────────────
    const monthSlots: Record<string, { month: string; invoiced: number; collected: number; costs: number }> = {};
    const today = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - 5 + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthSlots[key] = { month: key, invoiced: 0, collected: 0, costs: 0 };
    }
    for (const inv of invoices) {
      if (!inv.date) continue;
      const k = inv.date.slice(0, 7);
      if (monthSlots[k]) monthSlots[k].invoiced += N(inv.gross);
    }
    for (const r of receipts) {
      if (!r.date || r.tp === 'Advance mobilisation' || r.tp === 'Advance') continue;
      const k = r.date.slice(0, 7);
      if (monthSlots[k]) monthSlots[k].collected += N(r.amount);
    }
    for (const p of [...scPayments, ...supplierPayments]) {
      if (!p.date) continue;
      const k = p.date.slice(0, 7);
      if (monthSlots[k]) monthSlots[k].costs += N(p.amount);
    }
    for (const p of payrollAll) {
      if (!p.date) continue;
      const k = p.date.slice(0, 7);
      if (monthSlots[k]) {
        const net = N(p.basic) + N(p.house) + N(p.trans) + N(p.othr) - N(p.deduc);
        monthSlots[k].costs += net;
      }
    }
    const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const trendData = Object.values(monthSlots).sort((a,b) => a.month.localeCompare(b.month)).map(m => {
      const [y, mo] = m.month.split('-');
      return { ...m, month: `${MONTHS[parseInt(mo)-1]} ${y.slice(2)}` };
    });

    // ── Project rows ─────────────────────────────────────────────────────
    const activeProjects = projects.filter(p => p.status === 'Active');
    const projectRows = activeProjects.map(proj => {
      const pInv  = invoices.filter(i => i.projectId === proj.id);
      const pRcpt = receipts.filter(r => r.projectId === proj.id && r.tp !== 'Advance mobilisation' && r.tp !== 'Advance');
      const pSCInv = scInvoices.filter(i => i.projectId === proj.id);
      const pSCPay = scPayments.filter(p => p.projectId === proj.id && p.tp !== 'Advance');
      const pSuppInv = supplierInvoices.filter(i => i.projectId === proj.id);
      const pSuppPay = supplierPayments.filter(p => p.projectId === proj.id);
      const pPayroll = payrollAll.filter(p => p.projectId === proj.id);
      const pBudget = budgetLines.filter(b => b.projectId === proj.id);

      const contractVal = N(proj.ctrVal);
      const earned      = pInv.reduce((s, i) => s + N(i.gross), 0);
      const collected   = pRcpt.reduce((s, r) => s + N(r.amount), 0);
      const scCosts     = pSCInv.reduce((s, i) => s + N(i.gross), 0);
      const suppCosts   = pSuppInv.reduce((s, i) => s + N(i.gross), 0);
      const payrollCosts = pPayroll.reduce((s, p) => s + N(p.basic)+N(p.house)+N(p.trans)+N(p.othr)-N(p.deduc), 0);
      const directCosts = scCosts + suppCosts + payrollCosts;
      const margin      = earned > 0 ? ((earned - directCosts) / earned) * 100 : 0;
      const billingR    = contractVal > 0 ? (earned / contractVal) * 100 : 0;
      const collR       = earned > 0 ? (collected / earned) * 100 : 0;
      const arBal       = pInv.reduce((s, inv) => {
        const paid = pRcpt.filter(r => r.invRef === inv.id).reduce((a, r) => a + N(r.amount), 0);
        return s + Math.max(0, N(inv.gross) - paid);
      }, 0);
      const maxArDays   = pInv.reduce((mx, inv) => {
        const paid = pRcpt.filter(r => r.invRef === inv.id).reduce((a, r) => a + N(r.amount), 0);
        if (N(inv.gross) - paid <= 0.01) return mx;
        const d = inv.dueDate || inv.date;
        const days = d ? Math.max(0, Math.floor((now - new Date(d).getTime()) / 86400000)) : 0;
        return Math.max(mx, days);
      }, 0);
      const budgAmt     = pBudget.reduce((s, b) => s + N(b.appBgt), 0);
      const actlAmt     = pBudget.reduce((s, b) => s + N(b.actl), 0);
      const budgUtil    = budgAmt > 0 ? (actlAmt / budgAmt) * 100 : 0;
      const scPaid      = pSCPay.reduce((s, p) => s + N(p.amount), 0);
      const suppPaid    = pSuppPay.reduce((s, p) => s + N(p.amount), 0);
      const apBal       = Math.max(0, scCosts - scPaid) + Math.max(0, suppCosts - suppPaid);

      let status: keyof typeof STATUS_COLOR = 'Healthy';
      if (margin < 10 || maxArDays > 60 || budgUtil > 95) status = 'At Risk';
      else if (margin < 20 || maxArDays > 30 || budgUtil > 80) status = 'Watch';

      return { id: proj.id, name: proj.name, contractVal, earned, directCosts, margin, billingR, collR, arBal, maxArDays, budgUtil, apBal, status };
    });

    // ── At-risk alerts ────────────────────────────────────────────────────
    const atRiskAlerts: { proj: string; type: string; detail: string }[] = [];
    for (const row of projectRows) {
      if (row.margin < 10)      atRiskAlerts.push({ proj: row.name, type: 'Low Margin',      detail: `Gross margin at ${row.margin.toFixed(1)}% — below 10% threshold` });
      if (row.maxArDays > 60)   atRiskAlerts.push({ proj: row.name, type: 'AR Overdue',      detail: `AR outstanding for ${row.maxArDays} days — exceeds 60-day limit` });
      if (row.budgUtil > 95)    atRiskAlerts.push({ proj: row.name, type: 'Budget Critical', detail: `Budget utilization at ${row.budgUtil.toFixed(1)}% — near limit` });
    }

    return {
      totalContractValue, totalInvoiced, totalCollected, billingRate, collectionRate,
      grossMarginPct, dso, totalArBalance, totalApBalance, totalCostsPaid, totalDirectCosts,
      totalScClaims, totalSuppInv, totalPayroll, totalScPaid, totalSuppPaid,
      netCashPosition, arUnder60, netTaxPayable, taxCollected, taxPaidSC, taxPaidSupp,
      budgetUtil, totalBudget, totalActual,
      arAging, trendData, projectRows, atRiskAlerts,
      activeCount: activeProjects.length,
      atRiskCount: projectRows.filter(r => r.status === 'At Risk').length,
    };
  }, [projects, invoices, receipts, scInvoices, scPayments, supplierInvoices, supplierPayments, budgetLines, payrollAll]);

  const KpiCard = ({ icon: Icon, label, value, sub, color, iconBg }: {
    icon: React.ElementType; label: string; value: string; sub: string;
    color: string; iconBg: string;
  }) => (
    <Card className="p-5 rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest leading-tight">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{sub}</div>
    </Card>
  );

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold">CFO View</h2>
        <p className="text-muted-foreground text-sm mt-0.5">Portfolio-level financial overview for executive decision-making</p>
      </div>

      {/* ── Portfolio Summary Banner ─────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 text-white">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-200 mb-3">Portfolio Contract Value</p>
          <p className="text-3xl font-bold">{fc(c.totalContractValue, cur)}</p>
          <p className="text-sm text-blue-200 mt-1">{c.activeCount} active project{c.activeCount !== 1 ? 's' : ''}</p>
          <div className="mt-3 h-2 bg-blue-500/40 rounded-full overflow-hidden">
            <div className="h-full bg-white/80 rounded-full" style={{ width: `${Math.min(100, c.billingRate)}%` }} />
          </div>
          <p className="text-xs text-blue-200 mt-1">{c.billingRate.toFixed(1)}% billed · {fc(Math.max(0, c.totalContractValue - c.totalInvoiced), cur)} remaining</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-5 text-white">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-200 mb-3">Total Invoiced & Collected</p>
          <p className="text-3xl font-bold">{fc(c.totalInvoiced, cur)}</p>
          <p className="text-sm text-emerald-200 mt-1">Collected: {fc(c.totalCollected, cur)}</p>
          <div className="mt-3 h-2 bg-emerald-500/40 rounded-full overflow-hidden">
            <div className="h-full bg-white/80 rounded-full" style={{ width: `${Math.min(100, c.collectionRate)}%` }} />
          </div>
          <p className="text-xs text-emerald-200 mt-1">{c.collectionRate.toFixed(1)}% collection rate · AR {fc(c.totalArBalance, cur)}</p>
        </div>
        <div className={`rounded-2xl p-5 border-2 ${c.netCashPosition >= 0 ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' : 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800'}`}>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Net Cash Position</p>
          <p className={`text-3xl font-bold ${c.netCashPosition >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {c.netCashPosition >= 0 ? '+' : ''}{fc(c.netCashPosition, cur)}
          </p>
          <p className="text-sm text-muted-foreground mt-1">{c.netCashPosition >= 0 ? 'Inflows exceed outflows' : 'Outflows exceed inflows'}</p>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div><p className="text-muted-foreground">AR &lt;60d</p><p className="font-semibold text-emerald-600">{fc(c.arUnder60, cur)}</p></div>
            <div><p className="text-muted-foreground">AP Outstanding</p><p className="font-semibold text-rose-600">{fc(c.totalApBalance, cur)}</p></div>
          </div>
        </div>
      </div>

      {/* ── KPI Grid Row 1 ───────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard icon={TrendingUp}   label="Gross Margin"   value={`${c.grossMarginPct.toFixed(1)}%`}    sub="Portfolio gross margin" color={c.grossMarginPct >= 20 ? 'text-emerald-600' : c.grossMarginPct >= 10 ? 'text-amber-600' : 'text-rose-600'} iconBg={c.grossMarginPct >= 20 ? 'bg-emerald-50 dark:bg-emerald-950' : 'bg-amber-50 dark:bg-amber-950'} />
        <KpiCard icon={Clock}        label="DSO"             value={`${c.dso} days`}                      sub="Days Sales Outstanding"  color={c.dso <= 30 ? 'text-emerald-600' : c.dso <= 60 ? 'text-amber-600' : 'text-rose-600'}       iconBg={c.dso <= 30 ? 'bg-emerald-50 dark:bg-emerald-950' : 'bg-blue-50 dark:bg-blue-950'} />
        <KpiCard icon={DollarSign}   label="AR Balance"      value={fc(c.totalArBalance, cur)}            sub="Total receivables"       color="text-rose-600"   iconBg="bg-rose-50 dark:bg-rose-950" />
        <KpiCard icon={Receipt}      label="AP Balance"      value={fc(c.totalApBalance, cur)}            sub="SC + Supplier payables"  color="text-amber-600"  iconBg="bg-amber-50 dark:bg-amber-950" />
      </div>

      {/* ── KPI Grid Row 2 ───────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard icon={Activity}     label="Billing Rate"    value={`${c.billingRate.toFixed(1)}%`}       sub={`${fc(c.totalInvoiced, cur)} invoiced`}   color={c.billingRate >= 75 ? 'text-emerald-600' : c.billingRate >= 50 ? 'text-amber-600' : 'text-rose-600'} iconBg="bg-blue-50 dark:bg-blue-950" />
        <KpiCard icon={BarChart2}    label="Collection Rate" value={`${c.collectionRate.toFixed(1)}%`}    sub={`${fc(c.totalCollected, cur)} collected`}  color={c.collectionRate >= 80 ? 'text-emerald-600' : c.collectionRate >= 60 ? 'text-amber-600' : 'text-rose-600'} iconBg="bg-violet-50 dark:bg-violet-950" />
        <KpiCard icon={Briefcase}    label="Budget Util."    value={`${c.budgetUtil.toFixed(1)}%`}        sub={`${fc(c.totalActual, cur)} of ${fc(c.totalBudget, cur)}`} color={c.budgetUtil > 95 ? 'text-rose-600' : c.budgetUtil > 80 ? 'text-amber-600' : 'text-emerald-600'} iconBg="bg-slate-50 dark:bg-slate-950" />
        <KpiCard icon={ShieldAlert}  label="At-Risk Projects" value={`${c.atRiskCount} / ${c.activeCount}`} sub="Active projects at risk" color={c.atRiskCount > 0 ? 'text-rose-600' : 'text-emerald-600'} iconBg={c.atRiskCount > 0 ? 'bg-rose-50 dark:bg-rose-950' : 'bg-emerald-50 dark:bg-emerald-950'} />
      </div>

      {/* ── Charts Row: Trend + AR Aging ─────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-3 p-6 rounded-2xl border-border/50">
          <h3 className="font-bold text-base mb-0.5">6-Month Revenue Trend</h3>
          <p className="text-xs text-muted-foreground mb-4">Invoiced, collected & costs paid across all projects</p>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={c.trendData} margin={{ top: 4, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={tickFmt} />
              <Tooltip formatter={(v: number, name: string) => [fc(v, cur), name]} contentStyle={{ borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--card)' }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="invoiced"  name="Invoiced"   fill="#3b82f6" radius={[4,4,0,0]} />
              <Bar dataKey="collected" name="Collected"  fill="#10b981" radius={[4,4,0,0]} />
              <Line type="monotone" dataKey="costs" name="Costs Paid" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 4, fill: '#ef4444' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>

        <Card className="lg:col-span-2 p-6 rounded-2xl border-border/50">
          <h3 className="font-bold text-base mb-0.5">AR Aging Breakdown</h3>
          <p className="text-xs text-muted-foreground mb-4">Portfolio receivables by age bucket</p>
          {c.totalArBalance === 0 ? (
            <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
              <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
                <span className="text-emerald-600 text-2xl font-bold">✓</span>
              </div>
              <p className="font-medium">All AR Cleared</p>
              <p className="text-xs mt-1">No outstanding receivables</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[
                  { name: '0–30d',  amt: c.arAging.d30,  fill: '#10b981' },
                  { name: '31–60d', amt: c.arAging.d60,  fill: '#f59e0b' },
                  { name: '61–90d', amt: c.arAging.d90,  fill: '#f97316' },
                  { name: '90+d',   amt: c.arAging.d90p, fill: '#ef4444' },
                ]} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={tickFmt} />
                  <Tooltip formatter={(v: number) => [fc(v, cur), 'Outstanding']} contentStyle={{ borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--card)' }} />
                  <Bar dataKey="amt" radius={[4,4,0,0]}>
                    {[c.arAging.d30, c.arAging.d60, c.arAging.d90, c.arAging.d90p].map((_, i) => (
                      <Cell key={i} fill={['#10b981','#f59e0b','#f97316','#ef4444'][i]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[{ l: '0–30d', v: c.arAging.d30, color: '#10b981' },{ l: '31–60d', v: c.arAging.d60, color: '#f59e0b' },{ l: '61–90d', v: c.arAging.d90, color: '#f97316' },{ l: '90+d', v: c.arAging.d90p, color: '#ef4444' }]
                  .filter(b => b.v > 0).map(b => (
                    <div key={b.l} className="flex items-center gap-1.5 text-xs">
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ background: b.color }} />
                      <span className="text-muted-foreground">{b.l}:</span>
                      <span className="font-semibold">{fc(b.v, cur)}</span>
                    </div>
                  ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* ── Cost Breakdown + Project Table ───────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="p-6 rounded-2xl border-border/50">
          <h3 className="font-bold text-sm mb-0.5">Cost Composition</h3>
          <p className="text-xs text-muted-foreground mb-3">Portfolio direct cost mix</p>
          {c.totalDirectCosts === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">No cost data</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie data={[
                    { name: 'SC',       value: c.totalScClaims, fill: COST_COLORS.sc },
                    { name: 'Supplier', value: c.totalSuppInv,  fill: COST_COLORS.supplier },
                    { name: 'Payroll',  value: c.totalPayroll,  fill: COST_COLORS.payroll },
                  ].filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="value" paddingAngle={3}>
                    {[COST_COLORS.sc, COST_COLORS.supplier, COST_COLORS.payroll].map((fill, i) => <Cell key={i} fill={fill} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => [fc(v, cur), '']} contentStyle={{ borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--card)' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5">
                {[
                  { label: 'SC Claims',  value: c.totalScClaims, color: COST_COLORS.sc },
                  { label: 'Supplier',   value: c.totalSuppInv,  color: COST_COLORS.supplier },
                  { label: 'Payroll',    value: c.totalPayroll,  color: COST_COLORS.payroll },
                ].filter(d => d.value > 0).map(d => (
                  <div key={d.label} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-sm" style={{ background: d.color }} />
                      <span className="text-muted-foreground">{d.label}</span>
                    </div>
                    <span className="font-semibold">{((d.value / c.totalDirectCosts) * 100).toFixed(0)}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card className="lg:col-span-3 rounded-2xl border-border/50 overflow-hidden">
          <div className="px-6 py-4 border-b border-border/50">
            <h3 className="font-bold text-base">Project Health Matrix</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Active projects — profitability, billing, collection & risk</p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[150px]">Project</TableHead>
                  <TableHead className="text-right">Contract</TableHead>
                  <TableHead className="text-right">Earned</TableHead>
                  <TableHead className="text-right">Margin %</TableHead>
                  <TableHead className="text-right">Billing %</TableHead>
                  <TableHead className="text-right">Coll. %</TableHead>
                  <TableHead className="text-right">AR</TableHead>
                  <TableHead className="text-right">AP</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.projectRows.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No active projects</TableCell></TableRow>
                ) : c.projectRows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium text-sm">{row.name}</TableCell>
                    <TableCell className="text-right text-xs">{fc(row.contractVal, cur)}</TableCell>
                    <TableCell className="text-right text-xs">{fc(row.earned, cur)}</TableCell>
                    <TableCell className={`text-right text-xs font-semibold ${row.margin >= 20 ? 'text-emerald-600' : row.margin >= 10 ? 'text-amber-600' : 'text-rose-600'}`}>
                      {row.margin.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      <div className="flex items-center justify-end gap-1.5">
                        <div className="w-12 h-1.5 bg-border/40 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(100, row.billingR)}%` }} />
                        </div>
                        <span>{row.billingR.toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      <div className="flex items-center justify-end gap-1.5">
                        <div className="w-12 h-1.5 bg-border/40 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${row.collR >= 80 ? 'bg-emerald-500' : row.collR >= 60 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${Math.min(100, row.collR)}%` }} />
                        </div>
                        <span>{row.collR.toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell className={`text-right text-xs ${row.arBal > 0 ? 'text-rose-600' : 'text-muted-foreground'}`}>{fc(row.arBal, cur)}</TableCell>
                    <TableCell className={`text-right text-xs ${row.apBal > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>{fc(row.apBal, cur)}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className={`text-xs ${STATUS_COLOR[row.status]}`}>{row.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      {/* ── At-Risk Alerts (Pro+) ────────────────────────────────────── */}
      {isProOrGroup ? (
        <Card className="rounded-2xl border-border/50 overflow-hidden">
          <div className="px-6 py-4 border-b border-border/50 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-rose-500" />
            <h3 className="font-bold text-base">At-Risk Alerts</h3>
            {c.atRiskAlerts.length > 0 && (
              <span className="ml-auto text-xs font-semibold bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400 px-2 py-0.5 rounded-full">{c.atRiskAlerts.length}</span>
            )}
          </div>
          {c.atRiskAlerts.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-muted-foreground">No at-risk conditions detected across active projects</div>
          ) : (
            <div className="divide-y divide-border/50">
              {c.atRiskAlerts.map((a, i) => (
                <div key={i} className="flex gap-3 px-6 py-3 hover:bg-muted/30 transition-colors">
                  <div className="w-7 h-7 rounded-full bg-rose-500/15 flex items-center justify-center shrink-0 mt-0.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{a.proj} <span className="text-xs font-normal text-rose-500 ml-1">({a.type})</span></div>
                    <div className="text-xs text-muted-foreground mt-0.5">{a.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : (
        <UpgradeTeaser feature="At-Risk Alerts" requiredPlan="Pro" compact />
      )}

      {/* ── Cash Flow + Tax (Pro+) ───────────────────────────────────── */}
      {isProOrGroup ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-6 rounded-2xl border-border/50">
            <div className="flex items-center gap-2 mb-4">
              <Landmark className="w-4 h-4 text-blue-500" />
              <h3 className="font-bold text-base">Cash Flow Snapshot</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Estimated near-term cash position based on AR/AP</p>
            <div className="space-y-2 text-sm">
              {[
                { label: 'Est. Inflows (AR < 60 days)',    value: fc(c.arUnder60, cur),      color: 'text-emerald-600' },
                { label: 'Est. Outflows (Unpaid AP)',       value: fc(c.totalApBalance, cur), color: 'text-rose-600' },
                { label: 'Total Costs Paid (Cash Out)',     value: fc(c.totalCostsPaid, cur), color: 'text-amber-600' },
              ].map(r => (
                <div key={r.label} className="flex justify-between py-2 border-b border-border/30">
                  <span className="text-muted-foreground">{r.label}</span>
                  <span className={`font-semibold ${r.color}`}>{r.value}</span>
                </div>
              ))}
              <div className="flex justify-between py-2 font-bold text-base">
                <span>Net Cash Position</span>
                <span className={c.netCashPosition >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                  {c.netCashPosition >= 0 ? '+' : ''}{fc(c.netCashPosition, cur)}
                </span>
              </div>
            </div>
          </Card>
          {effectiveFeatures.taxConfig ? (
            <Card className="p-6 rounded-2xl border-border/50">
              <div className="flex items-center gap-2 mb-4">
                <Receipt className="w-4 h-4 text-violet-500" />
                <h3 className="font-bold text-base">Tax Liability Summary</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">VAT/Tax collected vs. paid across all invoices</p>
              <div className="space-y-2 text-sm">
                {[
                  { label: 'Tax Collected (Client Invoices)',       value: fc(c.taxCollected, cur),   color: 'text-emerald-600' },
                  { label: 'Tax Paid (SC Invoices)',                value: fc(c.taxPaidSC, cur),      color: 'text-rose-600' },
                  { label: 'Tax Paid (Supplier Invoices)',          value: fc(c.taxPaidSupp, cur),    color: 'text-rose-600' },
                ].map(r => (
                  <div key={r.label} className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">{r.label}</span>
                    <span className={`font-semibold ${r.color}`}>{r.value}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 font-bold text-base">
                  <span>Net Tax Payable</span>
                  <span className={c.netTaxPayable >= 0 ? 'text-amber-600' : 'text-emerald-600'}>{fc(c.netTaxPayable, cur)}</span>
                </div>
              </div>
            </Card>
          ) : (
            <UpgradeTeaser feature="Tax Liability Summary" requiredPlan="Pro (with Tax Config)" compact />
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <UpgradeTeaser feature="Cash Flow Snapshot" requiredPlan="Pro" compact />
          <UpgradeTeaser feature="Tax Liability Summary" requiredPlan="Pro" compact />
        </div>
      )}
    </div>
  );
}
